import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { __ } from 'i18n/index';
import describeRankedStep from 'react/match/rankedPanel/describeRankedStep';
import loadedCharacterOf, { loadedRolledCharacterOf } from 'react/match/rankedPanel/loadedCharacterOf';
import RolledCharacter from 'react/elements/character/RolledCharacter';
import useSeasonCharacters from 'react/match/rankedPanel/useSeasonCharacters';
import requestRematch from 'react/match/active/requestRematch';
import rehostReinvite from 'react/match/room/rehostReinvite';
import { closeMatchRoom } from 'react/redux/actions/matchRoom';
import { isDoublesMatch, myTeamOf, opponentsOf, teammatesOf, teamNameOf } from 'react/match/rankedPanel/matchTeams';
import describeLobby from 'react/match/lobby/describeLobby';
import LobbyClock from 'react/match/lobby/LobbyClock';
import { acceptPlayer, declinePlayer, startTeamMatch } from 'react/match/lobby/lobbyActions';
import InlineError from 'react/elements/InlineError';

// One line for the current match: the set score (sets only), the step's
// title, "vs {opponent} · Game n", and the one action the step offers. It
// reads the same step as the ranked panel (describeRankedStep). Steps that
// need a grid (picks, strikes, bans, the counterpick, a vote conflict, a
// dispute) link to the match panel: panelHref, with onOpenPanel(event) to
// handle the click in place. The bottom dock (react/dock/MatchDockBar) shows it.
//
// Doubles: both players of each team, "We won" / "{opponents} won", "Your
// team's turn" on stage steps, Rehost + Reinvite for the host of a finished
// match (no Rematch: the server refuses it), and the lobby's one-tap actions
// for the host (Accept {name} / Decline, Start match).

const PANEL_VERBS = {
	pick: () => __('Pick a character'),
	strike: () => __('Strike a stage'),
	ban: () => __('Ban a stage'),
	counterpick: () => __('Pick the stage'),
	conflict: (doubles) => (doubles ? __('Change our vote') : __('Change my vote')),
	disputed: () => __('Add details'),
};

// What the action area holds for a step: 'report', 'over', 'friendlies',
// 'panel', 'lobby' or null (waiting, nothing to do here).
export function statusActionOf(step, match) {
	if(step.kind === 'lobby') {
		return 'lobby';
	}
	if(step.kind === 'report') {
		return step.turn === 'mine' ? 'report' : null;
	}
	if(step.kind === 'over') {
		return 'over';
	}
	if(step.kind === 'friendlies') {
		return typeof match.isStarted !== 'function' || match.isStarted() ? 'friendlies' : null;
	}
	if(PANEL_VERBS[step.kind] && (step.turn === 'mine' || step.turn === 'alert')) {
		return 'panel';
	}
	return null;
}

// "Your turn: ban 2 stages" while the opponent waits on my move. Reporting
// is not a turn: both players report at once.
export function statusTitleOf(step, doubles = false) {
	const title = step.title || __('Your match');
	if(step.turn !== 'mine' || step.kind === 'report' || step.kind === 'lobby' || !step.title) {
		return title;
	}
	const lowered = title.charAt(0).toLowerCase() + title.slice(1);
	if(doubles && ['strike', 'ban', 'counterpick'].includes(step.kind)) {
		return __('Your team\'s turn: %s', lowered);
	}
	return __('Your turn: %s', lowered);
}

function useSending() {
	const [sending, setSending] = useState(false);
	const send = (request) => {
		if(sending) {
			return;
		}
		setSending(true);
		new Promise((resolve) => resolve(request()))
			.catch(() => {})
			.finally(() => setSending(false));
	};
	return [sending, send];
}

// A failed request, on its own line under the buttons that sent it.
function ActionError({ message }) {
	return <InlineError className='match-status__error' message={message} />;
}

function Portraits({ game, players }) {
	return players.map((player) => <PlayerPortrait key={player.id} game={game} player={player} />);
}

function ReportButtons({ match, opponent, doubles, myPlayers, opponents }) {
	const [sending, send] = useSending();
	if(doubles) {
		return (
			<React.Fragment>
				<button type='button' className='match-status__button match-status__button--team' disabled={sending} onClick={() => send(() => match.report(WIN_MATCH))}>
					<span className='match-status__pair' aria-hidden='true'><Portraits game={match.game} players={myPlayers} /></span>
					{__('We won')}
				</button>
				<button type='button' className='match-status__button match-status__button--team' disabled={sending} onClick={() => send(() => match.report(LOSE_MATCH))}>
					<span className='match-status__pair' aria-hidden='true'><Portraits game={match.game} players={opponents} /></span>
					{__('%s won', teamNameOf(opponents, __('They')))}
				</button>
			</React.Fragment>
		);
	}
	return (
		<React.Fragment>
			<button type='button' className='match-status__button' disabled={sending} onClick={() => send(() => match.report(WIN_MATCH))}>
				{__('I won')}
			</button>
			<button type='button' className='match-status__button' disabled={sending} onClick={() => send(() => match.report(LOSE_MATCH))}>
				{__('%s won', opponent?.username || __('They'))}
			</button>
		</React.Fragment>
	);
}

function isMyMatchToHost(match) {
	return !!match.player1 && typeof myUser !== 'undefined' && String(match.player1.id) === String(myUser?.id);
}

// A finished doubles match: Rehost + Reinvite for the host, Close for all.
function DoublesOverButtons({ match }) {
	const dispatch = useDispatch();
	const [rehosting, setRehosting] = useState(false);
	const [error, setError] = useState(null);
	const rehost = () => {
		setRehosting(true);
		setError(null);
		rehostReinvite(match).catch((reason) => {
			setRehosting(false);
			if(reason) {
				setError(String(reason));
			}
		});
	};
	return (
		<React.Fragment>
			{isMyMatchToHost(match) && (
				<button type='button' className='match-status__button match-status__button--primary' disabled={rehosting} onClick={rehost}>
					{__('Rehost + Reinvite')}
				</button>
			)}
			<button type='button' className='match-status__button' onClick={() => dispatch(closeMatchRoom(match))}>
				{__('Close')}
			</button>
			<ActionError message={error} />
		</React.Fragment>
	);
}

// The lobby host's one tap: answer the first join request, else Start match.
function LobbyButtons({ match, lobby }) {
	const [sending, send] = useSending();
	const [error, setError] = useState(null);
	const run = (request) => {
		setError(null);
		send(() => request().catch((reason) => {
			if(reason) {
				setError(String(reason));
			}
		}));
	};
	if(!lobby.iAmHost) {
		return null;
	}
	if(lobby.pending) {
		return (
			<React.Fragment>
				<button type='button' className='match-status__button match-status__button--go' disabled={sending} onClick={() => run(() => acceptPlayer(match, lobby.pending))}>
					{__('Accept %s', lobby.pending.username)}
				</button>
				<button type='button' className='match-status__button' disabled={sending} onClick={() => run(() => declinePlayer(match, lobby.pending))}>
					{__('Decline')}
				</button>
				<ActionError message={error} />
			</React.Fragment>
		);
	}
	return (
		<React.Fragment>
			<button
				type='button'
				className='match-status__button match-status__button--go'
				disabled={sending || !lobby.canStart}
				title={lobby.startNote || undefined}
				onClick={() => run(() => startTeamMatch(match))}
			>
				{__('Start match')}
			</button>
			<ActionError message={error} />
		</React.Fragment>
	);
}

function OverButtons({ match }) {
	const dispatch = useDispatch();
	const [rematchRequested, setRematchRequested] = useState(false);
	const [error, setError] = useState(null);
	const rematch = () => {
		setRematchRequested(true);
		setError(null);
		requestRematch(match).catch((reason) => {
			setRematchRequested(false);
			if(reason) {
				setError(String(reason));
			}
		});
	};
	return (
		<React.Fragment>
			{!match.is_cancelled && (
				<button type='button' className='match-status__button match-status__button--primary' disabled={rematchRequested} onClick={rematch}>
					{rematchRequested ? __('Rematch requested') : __('Rematch')}
				</button>
			)}
			<button type='button' className='match-status__button' onClick={() => dispatch(closeMatchRoom(match))}>
				{__('Close')}
			</button>
			<ActionError message={error} />
		</React.Fragment>
	);
}

function DonePlayingButton({ match }) {
	const [sending, send] = useSending();
	return (
		<button type='button' className='match-status__button' disabled={sending} onClick={() => send(() => match.report(FINISH_MATCH, null, null))}>
			{__('I\'m done playing')}
		</button>
	);
}

// A player's character for the game, with what it landed on if it was Random.
function PlayerPortrait({ game, player }) {
	const character = loadedCharacterOf(game, player);
	if(!character) {
		return null;
	}
	return (
		<RolledCharacter rolledCharacter={loadedRolledCharacterOf(game, player)}>
			<img className='match-status__portrait' src={character.image_url} alt={character.name} title={character.name} />
		</RolledCharacter>
	);
}

function LobbyStatusLine({ match, step, className }) {
	const lobby = describeLobby(match);
	return (
		<div className={`match-status ${className}`} data-turn={step.turn} data-kind={step.kind}>
			<div className='match-status__score'>
				<span className='match-status__pill match-status__pill--lobby' data-testid='match-status-score'>{__('LOBBY')}</span>
			</div>
			<div className='match-status__text'>
				<strong className='match-status__title' data-testid='match-status-title'>{statusTitleOf(step, true)}</strong>
				<small className='match-status__meta' data-testid='match-status-meta'>
					{[
						lobby.ranked ? __('Ranked doubles') : __('Doubles friendlies'),
						__('%s of %s in the lobby', lobby.joinedCount, lobby.required),
					].join(' · ')}
					{' · '}
					<LobbyClock match={match} />
				</small>
			</div>
			<div className='match-status__actions' data-testid='match-status-actions'>
				<LobbyButtons match={match} lobby={lobby} />
			</div>
		</div>
	);
}

export default function MatchStatusLine({ match, panelHref, onOpenPanel, className = '' }) {
	// The portraits need the season's characters in the cache; render again once they load.
	useSeasonCharacters(match);
	const step = describeRankedStep(match);
	if(step.kind === 'lobby') {
		return <LobbyStatusLine match={match} step={step} className={className} />;
	}
	const isSet = match.isASet();
	const doubles = isDoublesMatch(match);
	const myPlayers = myTeamOf(match);
	const opponents = opponentsOf(match);
	const me = myPlayers[0] || null;
	const opponent = opponents[0] || null;
	const action = statusActionOf(step, match);
	const game = match.game;

	const opponentName = teamNameOf(opponents, __('your opponent'));
	const teammates = teammatesOf(match);
	const meta = [doubles && teammates.length ? __('with %s vs %s', teamNameOf(teammates), opponentName) : __('vs %s', opponentName)];
	if(isSet && game && step.kind !== 'over') {
		meta.push(__('Game %s', game.game_number));
	}
	if(!isSet) {
		meta.push(__('Friendlies'));
	}

	return (
		<div className={`match-status ${className}`} data-turn={step.turn} data-kind={step.kind}>
			<div className='match-status__score'>
				{isSet && (doubles ? <Portraits game={game} players={myPlayers} /> : <PlayerPortrait game={game} player={me} />)}
				{isSet ? (
					<span className='match-status__pill' data-testid='match-status-score' data-done={step.kind === 'over' ? 'true' : undefined}>
						{match.getMyTeamWinCount() || 0}–{match.getOtherTeamWinCount() || 0}
					</span>
				) : (
					<span className='match-status__pill' data-done={step.kind === 'over' ? 'true' : undefined}>
						{step.kind === 'over' ? __('Over') : __('LIVE')}
					</span>
				)}
				{isSet && (doubles ? <Portraits game={game} players={opponents} /> : <PlayerPortrait game={game} player={opponent} />)}
			</div>
			<div className='match-status__text'>
				<strong className='match-status__title' data-testid='match-status-title'>{statusTitleOf(step, doubles)}</strong>
				<small className='match-status__meta' data-testid='match-status-meta'>{meta.join(' · ')}</small>
			</div>
			<div className='match-status__actions' data-testid='match-status-actions'>
				{action === 'report' && <ReportButtons match={match} opponent={opponent} doubles={doubles} myPlayers={myPlayers} opponents={opponents} />}
				{action === 'over' && (doubles ? <DoublesOverButtons match={match} /> : <OverButtons match={match} />)}
				{action === 'friendlies' && <DonePlayingButton match={match} />}
				{action === 'panel' && (
					<a className='match-status__button match-status__button--go' href={panelHref} onClick={onOpenPanel}>
						{PANEL_VERBS[step.kind](doubles)} <span aria-hidden='true'>▲</span>
					</a>
				)}
			</div>
		</div>
	);
}
